import { Search } from 'lucide-react';
import { useEffect } from 'react';

import { SelectableItem } from '~features/payment/ui/selectable-item';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { ScrollArea } from '~shared/shadcn/ui/scroll-area';

import { useExclusionModal } from '../hooks/use-exclusion-modal';

export type ExclusionType = 'sessions' | 'classes' | 'events' | 'parents';

interface ExclusionModalProps {
  type: ExclusionType | null;
  onClose: () => void;
}

export function ExclusionModal({ type, onClose }: ExclusionModalProps) {
  const {
    isOpen,
    title,
    searchTerm,
    setSearchTerm,
    filteredItems,
    selectedItems,
    toggleItem,
    selectAllFiltered,
    deselectAllFiltered,
    saveSelections,
    initializeModal,
  } = useExclusionModal(type, onClose);

  useEffect(() => {
    if (type) {
      initializeModal(type);
    }
  }, [type, initializeModal]);

  if (!type) return null;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className='flex h-[90vh] max-w-3xl flex-col'>
        <DialogHeader>
          <DialogTitle>Manage Excluded {title}</DialogTitle>
          <DialogDescription>
            Select {title.toLowerCase()} to exclude from late fee calculations
          </DialogDescription>
        </DialogHeader>

        <div className='my-4 flex grow flex-col space-y-4 overflow-hidden'>
          <div className='relative w-full'>
            <Search className='absolute left-2 top-2.5 size-4 text-muted-foreground' />
            <Input
              placeholder={`Search ${title.toLowerCase()}...`}
              className='w-full pl-8'
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
            />
          </div>

          <div className='flex items-center justify-between'>
            <div className='text-sm text-muted-foreground'>
              {filteredItems.length} {title.toLowerCase()} found
            </div>
            <div className='flex gap-2'>
              <Button variant='outline' size='sm' onClick={selectAllFiltered}>
                Select All
              </Button>
              <Button variant='outline' size='sm' onClick={deselectAllFiltered}>
                Deselect All
              </Button>
            </div>
          </div>

          <div className='flex grow flex-col overflow-hidden rounded-md border'>
            <ScrollArea className='h-[350px] w-full'>
              <div className='space-y-1 p-2'>
                {filteredItems.map((item) => (
                  <SelectableItem
                    key={item.value}
                    item={item}
                    isSelected={selectedItems.includes(item.value)}
                    onToggle={() => {
                      toggleItem(item.value);
                    }}
                  />
                ))}
              </div>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter>
          <div className='flex w-full justify-between'>
            <div className='text-sm'>
              {selectedItems.length} {title.toLowerCase()} selected
            </div>
            <div className='space-x-2'>
              <Button variant='outline' onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={saveSelections}>Apply</Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
